import jsPDF from 'jspdf';
import i18n from '../../../i18n';
import { SceneWithUserNames } from '../../../types/scene.types';
import { BreakdownInfo } from '../../../pages/Home/types';
import { setFont, registerFont, SupportedLanguage } from '../PDFExporter';
import { formatDayNightForExport, formatInteriorExteriorForExport } from '../exportSceneFieldFormat';
import { ElementInfo } from '../../../types/element.types';
import { ElementType } from '../../../types/element.types';
import { ElementDataCache, ElementRelationDataCache } from '../../../types/cache.types';
import { SceneCodeParts } from '../../../pages/Breakdown/BreakdownMain/Utils/SceneCodeUtils';
import { sortCastItemsByNO } from '../../../utils/elementSortUtils';
import { PdfScenesExportSort, sortScenesForPdfExport, DEFAULT_PDF_SCENES_EXPORT_SORT } from '../exportSceneSort';

const t = (key: string, opts?: { lng?: string }) => i18n.t(key, opts);

// 字体文件映射
const FONT_FILES = {
  'zh-CN': {
    name: 'AlibabaPuHuiTi'
  },
  'zh-HK': {
    name: 'AlibabaPuHuiTi'
  },
  'en': {
    name: 'AlibabaPuHuiTi'
  }
};

// 由于 jspdf-autotable 的类型问题，我们使用 require 动态导入
// @ts-ignore
const autoTable = require('jspdf-autotable').default;

// 颜色常量（十六进制格式）
const COLORS = {
  BLUE: '#5349AD',        // RGB: [83, 73, 173] - 蓝色背景
  WHITE: '#FFFFFF',       // RGB: [255, 255, 255] - 白色背景
  LIGHT_GRAY: '#F5F5F5',  // RGB: [245, 245, 245] - 浅灰色背景
  TEXT_WHITE: 255         // 白色文字（灰度值）
};

// 统一样式常量
const STYLES = {
  HEADER_FONT_SIZE: 8,        // header 字体大小
  HEADER_FONT_STYLE: 'normal', // header 字体样式
  TABLE_FONT_SIZE: 9,          // 表格字体大小（表头和内容）
  CELL_PADDING: 0.8,          // 单元格内边距（缩小60%，从2改为0.8）
  HEADER_Y: 10,               // header Y位置
  TABLE_START_Y: 14           // 表格起始Y位置（上移）
};

export const PDF_SCENES_SETS_A4_MARGINS_MM = {
  left: 7,
  right: 7,
  top: STYLES.TABLE_START_Y,
  bottom: 10
} as const;

// 将十六进制颜色转换为 RGB 数组
const hexToRgb = (hex: string): number[] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [0, 0, 0];
};

// 字段映射 - 从 i18n 根据语言获取
const getFieldMapForLanguage = (language: SupportedLanguage) => ({
  sceneCode: t('export.sceneCode', { lng: language }),
  interiorExterior: t('export.interiorExterior', { lng: language }),
  dayNight: t('export.dayNight', { lng: language }),
  sceneSet: t('export.sceneSet', { lng: language }),
  scenePlotDay: t('export.scenePlotDay', { lng: language }),
  scenePagesNumber: t('export.scenePagesNumber', { lng: language }),
  scenePlot: t('export.scenePlot', { lng: language }),
  sceneLocation: t('export.sceneLocation', { lng: language }),
  cast: t('export.cast', { lng: language }),
  scenePeriod: t('export.scenePeriod', { lng: language })
});


// 检查场景的所有字段是否都为空
const isSceneRowEmpty = (scene: SceneWithUserNames): boolean => {
  // 检查所有导出字段是否都为空
  const hasSceneCode = !!scene.sceneCode?.trim();
  const hasInteriorExterior = !!scene.sceneInteriorExterior;
  const hasDayNight = !!scene.sceneDayNight;
  const hasSceneSet = !!scene.sceneSet?.trim();
  const hasScenePlotDay = !!scene.scenePlotDay?.trim();
  const hasScenePagesNumber = !!scene.scenePagesNumber;
  const hasScenePlot = !!scene.scenePlot?.trim();
  const hasSceneLocation = !!scene.sceneLocation?.trim();
  const hasScenePeriod = !!scene.scenePeriod?.trim();
  
  // 如果所有字段都为空，则返回true（表示该行应该被过滤）
  return !hasSceneCode && 
         !hasInteriorExterior && 
         !hasDayNight && 
         !hasSceneSet && 
         !hasScenePlotDay && 
         !hasScenePagesNumber && 
         !hasScenePlot && 
         !hasSceneLocation && 
         !hasScenePeriod;
};

// 统一的header绘制函数
const addTitleHeader = (
  doc: jsPDF,
  breakdownInfo: BreakdownInfo,
  language: SupportedLanguage,
  optionName?: string,
  pageNumber?: number
) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const startX = 10; // 左边距
  const titleY = STYLES.HEADER_Y;
  
  // 从 localStorage 获取 FilmContext 存储的影片名称（filmName）
  const filmName = typeof window !== 'undefined' ? localStorage.getItem('selectedFilmName') : null;
  
  // 统一使用 header 字体样式
  doc.setFontSize(STYLES.HEADER_FONT_SIZE);
  setFont(doc, language, 'normal');
  doc.setTextColor(0, 0, 0);

  // 左侧：影片名称 | 分场名称
  const versionLabel = t('export.versionLabel', { lng: language });
  const leftTitleText = `《${filmName || ''}》     ${optionName || ''}              ${versionLabel}${breakdownInfo.breakdownName || ''}`;
  doc.text(leftTitleText, startX, titleY);
  
  // 右侧：剧本版本：XXX | 第 X 页
  const versionText = t('export.scriptVersion', { lng: language });
  const pagePrefix = t('export.pagePrefix', { lng: language });
  const pageSuffix = t('export.pageSuffix', { lng: language });
  
  const labelSeparator = t('common.labelSeparator', { lng: language }) as string;
  const pipeSeparator = t('common.pipeSeparator', { lng: language }) as string;
  let rightTitleText = `${versionText}${labelSeparator}${breakdownInfo.breakdownScriptOverview || ''}`;
  
  // 如果有页码，添加页码信息
  if (pageNumber !== undefined) {
    rightTitleText += `${pipeSeparator}${pagePrefix} ${pageNumber}${pageSuffix ? ' ' + pageSuffix : ''}`;
  }
  
  // 计算右侧文本的X位置（右对齐）
  const rightTextWidth = doc.getTextWidth(rightTitleText);
  const rightX = pageWidth - rightTextWidth - startX;
  doc.text(rightTitleText, rightX, titleY);
};

// 导出分场表A4 PDF
export const exportScenesTableA4 = async (
  doc: jsPDF, 
  scenes: SceneWithUserNames[], 
  breakdownInfo: BreakdownInfo,
  language: SupportedLanguage,
  secondaryLanguage: SupportedLanguage | undefined,
  sort: PdfScenesExportSort = DEFAULT_PDF_SCENES_EXPORT_SORT,
  optionName?: string,
  breakdownId?: string,
  getElementCache?: (breakdownId: string) => ElementDataCache | null,
  getElementRelationCache?: (breakdownId: string) => ElementRelationDataCache | null
): Promise<jsPDF> => {
  // 设置页面为横向A4
  doc = new jsPDF('l', 'mm', 'a4');
  
  // 重新注册字体（因为创建了新实例）
  const fontRegistered = await registerFont(doc, language);
  if (!fontRegistered) {
    console.warn(`字体注册失败，将使用默认字体。语言: ${language}`);
  }
  
  // 设置字体
  setFont(doc, language);
  
  // 在第一页绘制header
  addTitleHeader(doc, breakdownInfo, language, optionName, 1);
  
  // 准备表格数据
  // 创建场号解析缓存，提升排序性能（compareSceneCodes 内部会自动填充缓存）
  const parseCache = new Map<string, SceneCodeParts | null>();
  
  const sortedScenes = sortScenesForPdfExport(
    scenes.filter((scene) => !isSceneRowEmpty(scene)),
    sort,
    parseCache
  );
  
  // 表格头 - 使用当前APP语言显示
  const fieldMap = getFieldMapForLanguage(language);
  const headers = [
    fieldMap.sceneCode,
    fieldMap.interiorExterior,
    fieldMap.dayNight,
    fieldMap.sceneSet,
    fieldMap.scenePlotDay,
    fieldMap.scenePagesNumber,
    fieldMap.scenePlot,
    fieldMap.sceneLocation,
    fieldMap.cast,
    fieldMap.scenePeriod
  ];
  
  // 从缓存获取 elements 和 elementRelations
  const elementsBySceneId = new Map<string, ElementInfo[]>();
  
  if (breakdownId && getElementCache && getElementRelationCache) {
    const elementCache = getElementCache(breakdownId);
    const elementRelationCache = getElementRelationCache(breakdownId);
    
    const allElements = elementCache?.elements || [];
    const allRelations = elementRelationCache?.elementRelations || [];
    
    // 创建 elementID 到 ElementInfo 的映射
    const elementMap = new Map<string, ElementInfo>();
    allElements.forEach(item => {
      elementMap.set(item.elementID, item);
    });
    
    // 根据关系，将 elements 按 sceneID 分组，并合并关系中的数量信息
    allRelations.forEach(relation => {
      const element = elementMap.get(relation.elementID);
      if (element) {
        // 创建一个副本，并添加关系中的数量信息
        const enrichedItem = {
          ...element,
          elementQtyInScene: relation.elementQtyInScene || 1
        };
        const existing = elementsBySceneId.get(relation.sceneID) || [];
        elementsBySceneId.set(relation.sceneID, [...existing, enrichedItem]);
      }
    });
  }

  // 获取主要角色列文案（从缓存）
  const getCastColumnTextForScene = (sceneId: string): string => {
    const elements = elementsBySceneId.get(sceneId) || [];
    
    if (!Array.isArray(elements) || elements.length === 0) {
      return '';
    }
    
    const castItems = elements.filter(
      (item: ElementInfo) => item.elementType === ElementType.CAST
    );
    
    const sortedCastItems = sortCastItemsByNO(castItems);
    
    const roleNames = sortedCastItems
      .map((item: ElementInfo) => item.elementAbbr || item.elementName || '')
      .filter(Boolean);
    
    return roleNames.join('/') || '';
  };
  
  const castTextBySceneId = new Map<string, string>();
  sortedScenes.forEach((scene) => {
    const castColumnText = getCastColumnTextForScene(scene.sceneID);
    castTextBySceneId.set(scene.sceneID, castColumnText);
  });
  
  
  // 获取字体名称，如果字体注册失败则使用默认字体
  const fontName = fontRegistered ? FONT_FILES[language].name : 'helvetica';

  // 计算页面可用宽度（A4横向：297mm，减去左右边距28mm）
  const pageWidthForTable = doc.internal.pageSize.getWidth();
  const margin = PDF_SCENES_SETS_A4_MARGINS_MM.left; 
  const tableWidth = pageWidthForTable - (margin * 2); // 可用表格宽度

  // 根据百分比计算各列的实际宽度（毫米）
  // 注意：百分比总和必须为100%，当前调整为：
  const columnWidths = {
    0: tableWidth * 0.084,  // 场号
    1: tableWidth * 0.03,  // 内外（主次语言拼接后需要更宽）
    2: tableWidth * 0.04,  // 日夜（主次语言拼接后需要更宽）
    3: tableWidth * 0.16,  // 场景
    4: tableWidth * 0.05,  // 剧情日
    5: tableWidth * 0.03,  // 页数
    6: tableWidth * 0.188,  // 剧情
    7: tableWidth * 0.16,  // 拍摄地点
    8: tableWidth * 0.20,  // 主要角色
    9: tableWidth * 0.058   // 排期
  };

  // 表格数据：所有列保留完整内容，由 autoTable 按列宽自动换行
  const dataRows = sortedScenes.map((scene) => [
    scene.sceneCode || '',
    formatInteriorExteriorForExport(language, scene.sceneInteriorExterior, secondaryLanguage),
    formatDayNightForExport(language, scene.sceneDayNight, secondaryLanguage),
    scene.sceneSet || '',
    scene.scenePlotDay || '',
    scene.scenePagesNumber != null ? String(scene.scenePagesNumber) : '',
    scene.scenePlot || '',
    scene.sceneLocation || '',
    castTextBySceneId.get(scene.sceneID) || '',
    scene.scenePeriod || ''
  ]);

  // 计算页数总计
  const totalPages = sortedScenes.reduce((sum, scene) => {
    const pages = parseFloat(scene.scenePagesNumber?.toString() || '0') || 0;
    return sum + pages;
  }, 0);
  
  // 添加总计行：合并前5个单元格，合并后5个单元格（从页数列到排期列，索引5-9）
  const totalPageText = t('export.total', { lng: language });
  const pageUnitText = t('export.scenePagesCountUnit', { lng: language });
  const totalRow: any[] = [
    { content: '', colSpan: 5 }, // 合并前5个单元格（索引0-4）
    { content: `${totalPageText} ${totalPages.toFixed(1)} ${pageUnitText}`, colSpan: 5, styles: { halign: 'left', valign: 'middle' } } // 合并后5个单元格（索引5-9：页数、剧情、拍摄地点、主要角色、排期），显示总计
  ];
  
  // 使用autoTable生成表格
  doc.setFontSize(11);
  setFont(doc, language, 'normal');

  autoTable(doc, {
    head: [headers],
    body: [...dataRows, totalRow],
    startY: STYLES.TABLE_START_Y,
    theme: 'grid',
    rowPageBreak: 'avoid',
    margin: { left: margin, right: margin, bottom: 10 }, // 缩小底部边距
    tableWidth: tableWidth, // 明确指定表格总宽度
    showHead: 'everyPage', // 在每一页显示表头
    styles: {
      font: fontName,
      fontSize: STYLES.TABLE_FONT_SIZE, // 表格内容字体大小
      cellPadding: STYLES.CELL_PADDING,
      valign: 'middle', // 垂直居中对齐
      textColor: 0, // 正文纯黑，纸质打印更清晰
      lineColor: [0, 0, 0],
      lineWidth: 0.2
    },
    headStyles: {
      // 表头不要背景色 - 设置为白色背景色（透明效果）
      fillColor: hexToRgb(COLORS.WHITE), // 白色背景，等同于无背景色
      textColor: 0, // 黑色文字
      fontStyle: 'bold',
      font: fontName,
      fontSize: STYLES.TABLE_FONT_SIZE, // 表头字体大小
      valign: 'middle', // 垂直居中对齐
      halign: 'left',
      lineColor: [0, 0, 0],
      lineWidth: 0.2
    },
    // alternateRowStyles: {
    //   fillColor: hexToRgb(COLORS.LIGHT_GRAY) // 间隔色 - 已注释
    // },
    columnStyles: {
      0: { cellWidth: columnWidths[0] }, // 场号
      1: { cellWidth: columnWidths[1] }, // 内外
      2: { cellWidth: columnWidths[2] }, // 日夜
      3: { cellWidth: columnWidths[3] }, // 场景
      4: { cellWidth: columnWidths[4] }, // 剧情日
      5: { cellWidth: columnWidths[5] }, // 页数
      6: { cellWidth: columnWidths[6] }, // 剧情
      7: { cellWidth: columnWidths[7] }, // 拍摄地点
      8: { cellWidth: columnWidths[8] }, // 主要角色
      9: { cellWidth: columnWidths[9] }  // 排期
    },
    didParseCell: function(data: any) {
      // 表头样式调整 - 确保表头没有背景色
      if (data.row.section === 'head') {
        // 表头不要背景色 - 设置为白色背景色（透明效果）
        data.cell.styles.fillColor = hexToRgb(COLORS.WHITE); // 白色背景，等同于无背景色
        data.cell.styles.textColor = 0; // 黑色文字
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fontSize = STYLES.TABLE_FONT_SIZE; // 表头字体大小
        data.cell.styles.halign = 'left';
        data.cell.styles.valign = 'middle';
        data.cell.styles.overflow = 'hidden';
        data.cell.styles.lineColor = [0, 0, 0];
        data.cell.styles.lineWidth = 0.2;
      }
      // 防止body行被分页截断
      if (data.row.section === 'body') {
        data.cell.styles.overflow = 'linebreak';
        data.cell.styles.valign = 'top';
        data.cell.styles.lineColor = [0, 0, 0];
        data.cell.styles.lineWidth = 0.2;
      }
    },
    didDrawPage: function(data: any) {
      // 在每一页绘制 header（第一页已在表格绘制前绘制，这里处理后续页面）
      if (data.pageNumber > 1) {
        addTitleHeader(doc, breakdownInfo, language, optionName, data.pageNumber);
      }
    }
  });
  
  return doc;
};

